import { FC, useEffect, useState } from "react";
import { Box, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { IconCheck } from "@tabler/icons-react";

interface LoadingScreenProps {
  message?: string;
  // Quando informado, mostra um checklist com as etapas progredindo (usado só na
  // geração de estudo). Sem `steps`, é o loading simples (spinner) usado no resto
  // do sistema (auth, verificação de empresa, etc.).
  steps?: string[];
  // ms entre cada etapa (a última "segura" até a tela desmontar)
  stepIntervalMs?: number;
}

// Etapas exibidas enquanto um ESTUDO é gerado. Importe e passe via prop `steps`
// somente nos pontos que disparam a análise (NovaConsulta).
export const ESTUDO_STEPS = [
  "Analisando postos da rede",
  "Buscando preços de referência",
  "Cruzando abastecimentos",
  "Calculando cobertura e consumo",
  "Montando o mapa de rotas",
  "Finalizando o estudo",
];

export const LoadingScreen: FC<LoadingScreenProps> = ({
  message = "Carregando...",
  steps,
  stepIntervalMs = 1800,
}) => {
  const hasSteps = !!steps && steps.length > 0;
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!hasSteps) return;
    // Avança as etapas por tempo (o front não conhece o progresso real do backend);
    // segura na última até o componente desmontar (quando o resultado chega).
    const id = setInterval(() => {
      setCurrent((v) => Math.min(v + 1, (steps?.length ?? 1) - 1));
    }, stepIntervalMs);
    return () => clearInterval(id);
  }, [hasSteps, steps?.length, stepIntervalMs]);

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="white"
      zIndex={9999}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={6}
    >
      {hasSteps ? (
        <VStack spacing={6} align="stretch" maxW="360px" w="100%">
          <Text
            color="neutrals.text.sub-600"
            fontSize="sm"
            fontWeight={600}
            textAlign="center"
          >
            {message === "Carregando..." ? "Gerando o estudo…" : message}
          </Text>

          <VStack spacing={3} align="stretch">
            {steps!.map((label, idx) => {
              const done = idx < current;
              const active = idx === current;
              return (
                <HStack
                  key={label}
                  spacing={3}
                  opacity={done || active ? 1 : 0.45}
                  transition="opacity 0.3s ease"
                >
                  <Box
                    w="20px"
                    h="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    {done ? (
                      <Icon
                        as={IconCheck}
                        boxSize="18px"
                        color="states.success.base"
                      />
                    ) : active ? (
                      <Spinner
                        size="sm"
                        thickness="2px"
                        speed="0.65s"
                        color="primary.light.base"
                        emptyColor="neutrals.stroke.soft-200"
                      />
                    ) : (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg="neutrals.stroke.soft-200"
                      />
                    )}
                  </Box>
                  <Text
                    fontSize="sm"
                    color="neutrals.text.sub-600"
                    fontWeight={active ? 600 : 400}
                    transition="font-weight 0.3s ease"
                  >
                    {label}
                  </Text>
                </HStack>
              );
            })}
          </VStack>
        </VStack>
      ) : (
        <VStack spacing={4}>
          <Spinner
            size="xl"
            thickness="4px"
            speed="0.65s"
            color="primary.light.base"
            emptyColor="neutrals.stroke.soft-200"
          />
          <Text color="neutrals.text.sub-600" fontSize="sm">
            {message}
          </Text>
        </VStack>
      )}
    </Box>
  );
};
